// Databáze objednávek — sdílená data napříč tools.
// Zdroj: Shoptet Partner REST API. Cron sync každých 15 min + ruční backfill.
// Analytics modul z těchto dat jen čte.

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime, fmtDuration, fmtNum } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

type Props = {
  user: MeResponse;
};

type Order = {
  code: string;
  creation_time: string;
  status: string | null;
  total_with_vat: number | null;
  currency_code: string | null;
  customer_email: string | null;
  customer_country: string | null;
  is_cancelled: number;
  last_synced_at: number;
  item_count: number;
};

type OrdersRun = {
  id: number;
  run_at: number;
  status: 'success' | 'partial' | 'failed';
  orders_fetched: number;
  orders_upserted: number;
  from_time: string;
  to_time: string;
  duration_ms: number | null;
  error_message: string | null;
};

type OrdersStatus = {
  orders_count: number;
  oldest: string | null;
  newest: string | null;
  last_run: OrdersRun | null;
};

const PAGE_SIZE = 50;
// Shoptet eshop má objednávky až od léta 2025 (dřív v Shoptetu nejsou).
// Reálná data tedy táhneme od 1.7.2025.
const BACKFILL_FROM = '2025-07-01T00:00:00.000Z';

function fmtCzk(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(n)} Kč`;
}

function fmtOrderTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace('T', ' ');
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtOrderDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

export function OrderDatabasePage({ user }: Props) {
  const isAdmin = hasModule(user.modules, 'orders_db');
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [matched, setMatched] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [runs, setRuns] = useState<OrdersRun[]>([]);
  const [status, setStatus] = useState<OrdersStatus | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<null | string>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadOrders = useCallback(async (q: string, pageIdx: number) => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(pageIdx * PAGE_SIZE));
      const data = await apiFetch<{
        orders: Order[];
        total: number;
        matched: number;
      }>(`/api/orders?${params.toString()}`);
      setOrders(data.orders);
      setTotal(data.total);
      setMatched(data.matched);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání objednávek');
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [st, rn] = await Promise.all([
        apiFetch<OrdersStatus>('/api/orders/status'),
        apiFetch<{ runs: OrdersRun[] }>('/api/orders/runs'),
      ]);
      setStatus(st);
      setRuns(rn.runs);
    } catch {
      // meta není kritická
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // Debounce vyhledávání (300ms) + reset stránky
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      loadOrders(query, 0);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    loadOrders(query, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Chunked sync — auto-loop přes offset kvůli Cloudflare subrequest limitu.
  // Backend stahuje sériově a objednávky, které už v DB jsou, přeskakuje.
  async function runSync(opts: { label: string; query: string }) {
    if (syncing !== null) return;
    setSyncing(opts.label);
    setSyncMsg('Stahuju…');
    type Resp = {
      status: string;
      orders_upserted: number;
      orders_skipped?: number;
      duration_ms: number;
      total_in_range?: number;
      processed_so_far?: number;
      next_offset?: number | null;
      done?: boolean;
      error_message?: string | null;
    };
    let totalUpserted = 0;
    let totalSkipped = 0;
    let totalDuration = 0;
    let issues = 0;
    let offset = 0;
    try {
      for (let i = 0; i < 5000; i++) {
        const r = await apiFetch<Resp>(`/api/orders/sync?${opts.query}&offset=${offset}`, {
          method: 'POST',
        });
        totalUpserted += r.orders_upserted;
        totalSkipped += r.orders_skipped ?? 0;
        totalDuration += r.duration_ms;
        if (r.status !== 'success' && r.status !== 'skipped') issues++;
        const processed = r.processed_so_far ?? 0;
        const totalN = r.total_in_range ?? 0;
        const pct = totalN > 0 ? Math.min(100, Math.round((processed / totalN) * 100)) : 0;
        setSyncMsg(
          `Stahuju… ${pct} % (${processed}/${totalN}) — nové ${totalUpserted}, přeskočeno ${totalSkipped}`,
        );
        if (r.done || r.next_offset == null) break;
        offset = r.next_offset;
      }
      setSyncMsg(null);
      const secs = (totalDuration / 1000).toFixed(0);
      if (issues > 0) {
        setToast({
          msg: `Hotovo s výpadky — nové ${totalUpserted}, přeskočeno ${totalSkipped}. ${issues} dávek selhalo — klikni znovu, co je v DB se přeskočí.`,
          kind: 'error',
        });
      } else {
        setToast({
          msg: `✓ Hotovo — nově uloženo ${totalUpserted}, přeskočeno ${totalSkipped} (${secs}s)`,
          kind: 'success',
        });
      }
      await Promise.all([loadMeta(), loadOrders(query, page)]);
    } catch (e: unknown) {
      setSyncMsg(null);
      setToast({
        msg: e instanceof ApiError ? `Sync selhal: ${e.describe()}` : 'Sync selhal',
        kind: 'error',
      });
    } finally {
      setSyncing(null);
    }
  }

  function runBackfill() {
    // Doplníme jen CHYBĚJÍCÍ starší historii: okno [1.7.2025, nejstarší v DB].
    // Tím se backfill neproloopovává přes tisíce už uložených novějších
    // objednávek (jde rovnou na chybějící data) — spolehlivější a rychlejší.
    // Když je DB prázdná, bereme až do teď. Backend dedupuje upsertem na PK,
    // takže opakované spuštění po přerušení je levné.
    const to = status?.oldest ? new Date(status.oldest).toISOString() : new Date().toISOString();
    runSync({
      label: 'all',
      query: `from=${encodeURIComponent(BACKFILL_FROM)}&to=${encodeURIComponent(to)}`,
    });
  }

  const lastRun = status?.last_run ?? runs[0] ?? null;
  const pageCount = Math.max(Math.ceil(matched / PAGE_SIZE), 1);
  const fromIdx = matched === 0 ? 0 : page * PAGE_SIZE + 1;
  const toIdx = Math.min((page + 1) * PAGE_SIZE, matched);

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Databáze objednávek</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Objednávky ze Shoptet Partner REST API. Cron sync běží každých 15 minut, ručně lze doplnit
          zpětně. Slouží jako zdroj pro Analytics a další interní nástroje.
        </p>
      </div>

      {/* Stav DB */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Objednávek v DB" value={fmtNum(status?.orders_count ?? 0)} />
        <Stat label="Nejstarší v DB" value={fmtOrderDate(status?.oldest ?? null)} />
        <Stat
          label="Poslední sync"
          value={lastRun ? fmtDateTime(lastRun.run_at) : '—'}
          tone={
            lastRun?.status === 'success'
              ? 'success'
              : lastRun?.status === 'failed'
                ? 'danger'
                : 'default'
          }
        />
        <Stat label="Délka" value={lastRun ? fmtDuration(lastRun.duration_ms) : '—'} />
      </div>

      {lastRun?.error_message && lastRun.status !== 'success' && (
        <div className="text-sm text-warning bg-warning-light border border-warning/30 rounded-lg px-4 py-3 mb-4">
          <strong>Poslední běh hlásil:</strong> {lastRun.error_message.slice(0, 300)}
        </div>
      )}

      {/* Sync ovládání — jen admin */}
      {isAdmin && (
        <div className="bg-surface rounded-xl shadow-card p-4 mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-text-secondary mr-1">Stáhnout zpětně:</span>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => runSync({ label: String(d), query: `days=${d}` })}
                disabled={syncing !== null}
                className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-3 py-1.5 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing === String(d) ? `Sync ${d}d…` : `${d} dní`}
              </button>
            ))}
            <button
              type="button"
              onClick={runBackfill}
              disabled={syncing !== null}
              className="text-xs font-medium text-primary hover:text-white bg-primary/10 hover:bg-primary border border-primary/30 rounded px-3 py-1.5 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing === 'all' ? 'Doplňuju historii…' : 'Doplnit historii od 1.7.2025'}
            </button>
            <span className="text-[11px] text-text-muted ml-2">
              (doplní jen chybějící starší objednávky 1.7.2025 → nejstarší v DB; stahuje se sériově,
              ať Shoptet nehlásí limit. Nech tab otevřený. Po přerušení klikni znovu — co už je v
              DB, se přeskočí.)
            </span>
          </div>
          {syncMsg && <div className="text-xs mt-2 text-text-secondary">{syncMsg}</div>}
        </div>
      )}

      {/* Vyhledávač + počitadlo */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Vyhledat podle kódu objednávky nebo e-mailu"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 min-w-[280px] max-w-md text-sm text-text placeholder:text-text-muted bg-surface border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
        />
        <div className="text-sm text-text-secondary">
          {query ? (
            <>
              Nalezeno <strong className="text-text">{fmtNum(matched)}</strong> / {fmtNum(total)}{' '}
              objednávek
            </>
          ) : (
            <>
              Zobrazeno{' '}
              <strong className="text-text">
                {fromIdx}–{toIdx}
              </strong>{' '}
              z {fmtNum(total)}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Tabulka objednávek */}
      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg border-b border-border text-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3 w-32">Kód</th>
              <th className="text-left font-semibold px-4 py-3 w-44">Datum</th>
              <th className="text-left font-semibold px-4 py-3">Zákazník</th>
              <th className="text-left font-semibold px-4 py-3 w-28">Stav</th>
              <th className="text-right font-semibold px-4 py-3 w-24">Položek</th>
              <th className="text-right font-semibold px-4 py-3 w-32">Cena</th>
              <th className="text-right font-semibold px-4 py-3 w-40">Sync</th>
            </tr>
          </thead>
          <tbody>
            {orders === null && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted py-8">
                  Načítám…
                </td>
              </tr>
            )}
            {orders && orders.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-text-muted py-8">
                  {query ? 'Žádné výsledky pro daný dotaz.' : 'Databáze je prázdná. Spusť sync.'}
                </td>
              </tr>
            )}
            {orders?.map((o) => (
              <tr key={o.code} className="border-b border-border last:border-0 hover:bg-bg/50">
                <td className="px-4 py-2 font-mono text-text font-semibold">{o.code}</td>
                <td className="px-4 py-2 text-text">{fmtOrderTime(o.creation_time)}</td>
                <td className="px-4 py-2 text-text">
                  {o.customer_email ?? '—'}
                  {o.customer_country && (
                    <span className="text-text-muted text-xs ml-1.5">{o.customer_country}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {o.is_cancelled ? (
                    <span className="text-xs font-medium text-danger">
                      ✕ {o.status ?? 'Storno'}
                    </span>
                  ) : (
                    <span className="text-xs text-text-secondary">{o.status ?? '—'}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-text">{o.item_count}</td>
                <td className="px-4 py-2 text-right text-text font-medium">
                  {fmtCzk(o.total_with_vat)}
                </td>
                <td className="px-4 py-2 text-right text-text-muted text-xs">
                  {fmtDateTime(o.last_synced_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      {matched > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-text-secondary">
            Strana <strong className="text-text">{page + 1}</strong> /{' '}
            <strong className="text-text">{pageCount}</strong>
          </div>
          <div className="flex items-center gap-1">
            <PagerBtn onClick={() => setPage(0)} disabled={page === 0} label="«" />
            <PagerBtn
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
              label="‹ Předchozí"
            />
            <PagerBtn
              onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
              disabled={page >= pageCount - 1}
              label="Další ›"
            />
            <PagerBtn
              onClick={() => setPage(pageCount - 1)}
              disabled={page >= pageCount - 1}
              label="»"
            />
          </div>
        </div>
      )}

      {/* Historie běhů */}
      {runs.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-text mb-3">Historie sync běhů</h2>
          <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-border text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">Čas</th>
                  <th className="text-left font-semibold px-4 py-2.5">Stav</th>
                  <th className="text-right font-semibold px-4 py-2.5">Načteno</th>
                  <th className="text-right font-semibold px-4 py-2.5">Uloženo</th>
                  <th className="text-right font-semibold px-4 py-2.5">Délka</th>
                  <th className="text-left font-semibold px-4 py-2.5">Chyba</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-text">{fmtDateTime(r.run_at)}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-2 text-right text-text">{fmtNum(r.orders_fetched)}</td>
                    <td className="px-4 py-2 text-right text-text">{fmtNum(r.orders_upserted)}</td>
                    <td className="px-4 py-2 text-right text-text-muted">
                      {fmtDuration(r.duration_ms)}
                    </td>
                    <td className="px-4 py-2 text-text-muted text-xs truncate max-w-xs">
                      {r.error_message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function PagerBtn({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-sm font-medium text-text bg-surface border border-border rounded-lg px-3 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
    >
      {label}
    </button>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'danger' | 'default';
}) {
  const valCls =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-text';
  return (
    <div className="bg-surface rounded-xl p-4 shadow-card">
      <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 ${valCls}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'success' | 'partial' | 'failed' }) {
  if (status === 'success') {
    return (
      <span className="text-xs font-semibold text-success bg-success-light px-2 py-0.5 rounded">
        ✓ OK
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="text-xs font-semibold text-warning bg-warning-light px-2 py-0.5 rounded">
        ⚠ Částečně
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold text-danger bg-danger-light px-2 py-0.5 rounded">
      ✕ Selhalo
    </span>
  );
}
